// @flow
import * as React from 'react';
import { List } from 'immutable';
import classnames from 'classnames';
import { Dialog } from 'common/components/core/dialog';
import { themr } from 'react-css-themr';
import { translate } from 'react-i18next';
import DataUtils from 'common/utils/DataUtils';
import { FileDropzoneWithProgress } from 'common/components/core/dropzone';
import defaultTheme from './EntityImportView.scss';
import EntityValidationTable from 'common/entity/generic/EntityValidationTable';
import EntityIdValidationTable from 'common/entity/generic/EntityIdValidationTable';
import EntityUploadInfo from 'common/entity/generic/EntityUploadInfo';
import { EntityImportTable } from 'common/models/internal/EntityImportTable';
import { ButtonTypes } from 'common/components/core/button';
import { State } from 'react-automata';
import type { EntityImportStepType } from 'common/entity/helpers/EntityImportHelper';
import { EntityImportSteps } from 'common/entity/helpers/EntityImportHelper';
import { mapState, matchesState } from 'xstate';
import { Loader, Flex } from '@locus-taxy/neo-ui';

type OnFinishParams = {
    verifiedRowIds: ?Array<string>,
    entitiesToUpload: ?List<*>
};
type Props = {
    theme: Object,
    isUploadSuccessful: boolean,
    isUploadInProgress: boolean,
    isUploadFailed: boolean,
    fileName?: string,
    children: React.Node,
    currentStep?: number,
    uploadErrors?: ?Array<string>,
    onResetImport: () => void,
    uploadedTable: ?EntityImportTable,
    existingEntitiesTable: ?EntityImportTable,
    newEntitiesTable: ?EntityImportTable,
    otherValidationsTable: ?EntityImportTable,
    verifiedRowIds: ?Array<string>,
    ignoredRowIds: ?Array<string>,
    entityNameId: string,
    entitiesWithErrors: ?List<*>,
    entitiesToUpload: ?List<*>,
    allValidatedEntities: ?List<*>,
    entityErrors: ?List<*>,
    editedCells: Array<Object>,
    bulkValidationInProgress: boolean,
    onTableEdit: Function,
    onDuplicatesSelectionChange: Function,
    getRowValidationMessage: Function,
    onDismiss: () => void,
    onNext: () => void,
    transformHeader?: string => string,
    showNonImportableColumns: boolean,
    hasNonImportableColumns: boolean,
    onToggleNonImportableColumns: (active: boolean) => void,
    getEntityErrorCount: Function,
    getTotalErrorCount: Function,
    updateCellCoordsAndGetError: Function,
    getErrorByKey: Function,
    tableLastRefreshed: string,
    onCSVDrop: Function,
    doNotAllowNewEntry?: boolean,
    onFinish?: OnFinishParams => void,
    steps: Array<EntityImportStepType>,
    actions?: Array<{ label: string, onClick: () => void }>
};

class EntityImportView extends React.PureComponent<Props> {
    static defaultProps = {
        steps: EntityImportSteps
    };
    entityProcessingData = {
        showBack: true,
        showNext: true,
        showReset: true,
        showSteps: true,
        acceptActionLabel: this.props.t('translations:next'),
        cancelActionLabel: true,
        isNextDisabled: false,
        currentStep: 2,
        onNext: this.props.onNext,
        showNonImportableColumnToggler: false,
        showRefresh: false
    };

    fileUploadData = {
        showBack: false,
        showNext: false,
        showReset: false,
        currentStep: 1,
        onNext: this.props.onNext
    };

    registerWorkerData = {
        showBack: false,
        showNext: false,
        showReset: false
    };

    handleFinish = (...arg) => {
        const {
            entitiesToUpload,
            onFinish,
            onDismiss,
            verifiedRowIds
        } = this.props;
        onDismiss(...arg);
        onFinish?.({ entitiesToUpload, verifiedRowIds });
    };

    stateDataMap = {
        entityProcessing: this.entityProcessingData,
        fileUpload: this.fileUploadData,
        registerWorker: this.registerWorkerData,
        'entityProcessing.entityValidations.existingIdsFetch': {
            ...this.entityProcessingData,
            showBack: false,
            showNext: false
        },
        'entityProcessing.entityValidations.idValidations': {
            ...this.entityProcessingData,
            showBack: false,
            showNonImportableColumnToggler: true
        },
        'entityProcessing.entityValidations.idValidations.markIds': {
            ...this.entityProcessingData,
            showBack: false,
            isNextDisabled: false,
            nextDisabledTooltip: this.props.t(
                'entityImport.pleaseMarkExistingEntities',
                { entityNameId: this.props.entityNameId }
            ),
            showNonImportableColumnToggler: true
        },
        'entityProcessing.entityValidations.idValidations.noIdsToMark': {
            ...this.entityProcessingData,
            showBack: false,
            isNextDisabled: false,
            showNonImportableColumnToggler: true
        },
        'entityProcessing.entityValidations.idValidations.noIdsToUpload': {
            ...this.entityProcessingData,
            showBack: false,
            isNextDisabled: true,
            nextDisabledTooltip: this.props.t(
                'entityImport.noEntitiesToUpdate',
                { entityNameId: this.props.entityNameId }
            ),
            showNonImportableColumnToggler: true
        },
        'entityProcessing.entityValidations.idValidations.allIdsIgnored': {
            ...this.entityProcessingData,
            showBack: false,
            isNextDisabled: true,
            nextDisabledTooltip: this.props.t(
                'entityImport.noEntitiesToUpdate',
                { entityNameId: this.props.entityNameId }
            ),
            showNonImportableColumnToggler: true
        },
        'entityProcessing.entityValidations.otherValidations.fetchingOtherValidationTable': {
            ...this.entityProcessingData,
            currentStep: 3,
            isNextDisabled: true,
            showBack: false
        },
        'entityProcessing.entityValidations.otherValidations.showValidationTable': {
            ...this.entityProcessingData,
            currentStep: 3,
            showNonImportableColumnToggler: true,
            showRefresh: true,
            ...(this.props.doNotAllowNewEntry ? { showBack: false } : {})
        },
        'entityProcessing.upload': {
            ...this.entityProcessingData,
            currentStep: 4
        },
        'entityProcessing.upload.noUpload': {
            ...this.entityProcessingData,
            isNextDisabled: true,
            currentStep: 4,
            nextDisabledTooltip: this.props.t(
                'entityImport.noEntitiesToUpdate',
                { entityNameId: this.props.entityNameId }
            )
        },
        'entityProcessing.upload.uploading': {
            ...this.entityProcessingData,
            showBack: false,
            isResetDisabled: true,
            currentStep: 4,
            isNextDisabled: true
        },
        'entityProcessing.upload.success': {
            ...this.entityProcessingData,
            acceptActionLabel: this.props.t('translations:finish'),
            currentStep: 4,
            showBack: false,
            showSteps: false,
            onNext: this.handleFinish
        }
    };
    renderStep() {
        const {
            theme,
            isUploadFailed,
            isUploadSuccessful,
            fileName,
            uploadErrors,
            existingEntitiesTable,
            newEntitiesTable,
            reservedEntitiesTable,
            otherValidationsTable,
            verifiedRowIds,
            ignoredRowIds,
            entityNameId,
            entitiesWithErrors,
            entitiesToUpload,
            allValidatedEntities,
            entityErrors,
            editedCells,
            onTableEdit,
            onDuplicatesSelectionChange,
            getRowValidationMessage,
            onDownloadIgnoredEntities,
            onDownloadEntitiesWithErrors,
            onUploadTryAgain,
            machineState,
            onDownloadTemplate,
            transformHeader,
            showNonImportableColumns,
            getEntityErrorCount,
            getTotalErrorCount,
            getErrorByKey,
            updateCellCoordsAndGetError,
            tableLastRefreshed,
            isFileBasedEntity,
            parentEntityNameId,
            onCSVDrop,
            fetchDataValidationsPage,
            onDownloadReservedEntities,
            conflictEntities,
            doNotAllowNewEntry,
            actions
        } = this.props;
        const isValidatingIds = matchesState(
            machineState,
            'entityProcessing.entityValidations.existingIdsFetch.validating'
        );
        const bulkValidationInProgress = matchesState(
            machineState,
            'entityProcessing.entityValidations.otherValidations.fetchingOtherValidationTable'
        );
        const isFileUploadInProgress = matchesState(
            machineState,
            'fileUpload.uploading'
        );
        const isAddEntitiesInProgress = matchesState(
            machineState,
            'entityProcessing.upload.uploading'
        );
        const isFetchSummaryDataInProgress = matchesState(
            machineState,
            'entityProcessing.upload.fetchData'
        );
        const isAddEntitiesSuccessful = matchesState(
            machineState,
            'entityProcessing.upload.success'
        );

        return (
            <div className={theme.dialogStepsWrap}>
                <State value="registerWorker.*">
                    <Flex
                        sx={{
                            width: '100%'
                        }}
                        alignItems="center"
                        justifyContent="center"
                    >
                        <Loader size="large" color="blue.500" />
                    </Flex>
                </State>
                <State value="fileUpload.*">
                    <FileDropzoneWithProgress
                        isUploadInProgress={isFileUploadInProgress}
                        isUploadFailed={isUploadFailed}
                        isUploadSuccessful={isUploadSuccessful}
                        accept=".csv"
                        theme={theme}
                        fileName={fileName}
                        uploadErrors={uploadErrors}
                        onCancel={onUploadTryAgain}
                        onGetTemplateClick={onDownloadTemplate}
                        transformHeader={transformHeader}
                        onDrop={onCSVDrop}
                        actions={actions}
                    />
                </State>
                <State
                    value={[
                        'entityProcessing.entityValidations.existingIdsFetch.*',
                        'entityProcessing.entityValidations.idValidations.*',
                        'entityProcessing.entityValidations.removeIds.*'
                    ]}
                >
                    <EntityIdValidationTable
                        theme={theme}
                        existingEntitiesTable={existingEntitiesTable}
                        newEntitiesTable={newEntitiesTable}
                        reservedEntitiesTable={reservedEntitiesTable}
                        verifiedRowIds={verifiedRowIds}
                        ignoredRowIds={ignoredRowIds}
                        onDuplicatesSelectionChange={
                            onDuplicatesSelectionChange
                        }
                        entityNameId={entityNameId}
                        isValidatingIds={isValidatingIds}
                        showNonImportableColumns={showNonImportableColumns}
                        conflictEntities={conflictEntities}
                    />
                </State>
                <State value="entityProcessing.entityValidations.otherValidations.*">
                    <EntityValidationTable
                        onTableEdit={onTableEdit}
                        editedCells={editedCells}
                        allValidatedEntities={allValidatedEntities}
                        entityErrors={entityErrors}
                        otherValidationsTable={otherValidationsTable}
                        getRowValidationMessage={getRowValidationMessage}
                        entitiesWithErrors={entitiesWithErrors}
                        entityNameId={entityNameId}
                        theme={theme}
                        bulkValidationInProgress={bulkValidationInProgress}
                        showNonImportableColumns={showNonImportableColumns}
                        getEntityErrorCount={getEntityErrorCount}
                        getTotalErrorCount={getTotalErrorCount}
                        getErrorByKey={getErrorByKey}
                        updateCellCoordsAndGetError={
                            updateCellCoordsAndGetError
                        }
                        tableLastRefreshed={tableLastRefreshed}
                        fetchPage={fetchDataValidationsPage}
                    />
                </State>
                <State value="entityProcessing.upload.*">
                    <EntityUploadInfo
                        isUploadSuccessful={isAddEntitiesSuccessful}
                        isUploadInProgress={isAddEntitiesInProgress}
                        entitiesToUpload={entitiesToUpload}
                        entitiesWithErrors={entitiesWithErrors}
                        verifiedRowIds={verifiedRowIds}
                        ignoredRowIds={ignoredRowIds}
                        reservedEntitiesTable={reservedEntitiesTable}
                        entityNameId={entityNameId}
                        onDownloadEntitiesWithErrors={
                            onDownloadEntitiesWithErrors
                        }
                        onDownloadIgnoredEntities={onDownloadIgnoredEntities}
                        isFileBasedEntity={isFileBasedEntity}
                        parentEntityNameId={parentEntityNameId}
                        isFetchSummaryDataInProgress={
                            isFetchSummaryDataInProgress
                        }
                        onDownloadReservedEntities={onDownloadReservedEntities}
                        conflictEntities={conflictEntities}
                        doNotAllowNewEntry={doNotAllowNewEntry}
                    />
                </State>
            </div>
        );
    }
    getDataFromMachineState = machineState => {
        const machineStateString = machineState.toString();
        const {
            acceptActionLabel,
            currentStep,
            showNext,
            showBack,
            showReset,
            showSteps,
            nextDisabledTooltip,
            isNextDisabled,
            isResetDisabled,
            onNext,
            showNonImportableColumnToggler,
            showRefresh
        } = mapState(this.stateDataMap, machineStateString);
        return {
            isNextDisabled,
            acceptActionLabel,
            showSteps,
            showReset,
            showBack,
            showNext,
            currentStep,
            nextDisabledTooltip,
            onNext,
            isResetDisabled,
            showNonImportableColumnToggler,
            showRefresh
        };
    };
    render() {
        const {
            theme,
            onDismiss,
            onBack,
            t,
            showResetConfirmationDialog,
            onResetImport,
            onResetClick,
            onResetCancel,
            fileName,
            machineState,
            entityNameId,
            hasNonImportableColumns,
            onToggleNonImportableColumns,
            showNonImportableColumns,
            onTableRefresh,
            bulkValidationInProgress,
            existingEntitiesTable,
            newEntitiesTable,
            otherValidationsTable,
            reservedEntitiesTable,
            steps
        } = this.props;
        const {
            isNextDisabled: _isNextDisabled,
            showBack,
            showNext,
            showReset,
            showSteps,
            acceptActionLabel,
            currentStep,
            nextDisabledTooltip,
            onNext,
            isResetDisabled,
            showNonImportableColumnToggler,
            showRefresh
        } = this.getDataFromMachineState(machineState);
        const isNextDisabled = _isNextDisabled || bulkValidationInProgress;
        const isBackDisabled = bulkValidationInProgress || false;
        const currentStepClass = !DataUtils.isEmpty(currentStep)
            ? theme[`step${currentStep}`]
            : '';
        const hasTable =
            existingEntitiesTable?.rows.size ||
            newEntitiesTable?.rows.size ||
            reservedEntitiesTable?.rows.size ||
            otherValidationsTable?.rowsSize;
        return (
            <Dialog
                active
                title={
                    fileName
                        ? t('entityImport.importEntityFileName', {
                              fileName,
                              entityNameId
                          })
                        : t('entityImport.importEntity', {
                              entityNameId
                          })
                }
                onDismiss={onDismiss}
                className={theme.entityImportDialog}
                dialogClassName={classnames(theme.dialog, {
                    [theme.hasTable]: !!hasTable,
                    [currentStepClass]: currentStepClass ? true : false
                })}
                currentStep={currentStep}
                showSteps={showSteps}
                steps={steps}
                disableAccept={isNextDisabled}
                disableReset={isResetDisabled}
                disableCancel={isBackDisabled}
                onAccept={showNext && onNext}
                onCancel={showBack && onBack}
                onReset={showReset && onResetClick}
                resetIcon="file-upload"
                onRefresh={showRefresh && onTableRefresh}
                refreshActionLabel={t('entityImport.refreshTable')}
                cancelButtonType={ButtonTypes.Icon}
                cancelButtonIcon="nav-arrow-left"
                cancelActionLabel=""
                resetActionLabel={t('entityImport.importAgain')}
                acceptActionLabel={acceptActionLabel}
                acceptActionTooltip={isNextDisabled && nextDisabledTooltip}
                theme={theme}
                onToggleNonImportableColumns={
                    showNonImportableColumnToggler &&
                    onToggleNonImportableColumns
                }
                disableNonImportableToggle={!hasNonImportableColumns}
                showNonImportableColumns={showNonImportableColumns}
                nonImportableTooltip={t(
                    'entityImport.nonImportableTooltipMessage'
                )}
                nonImportablePrefixLabel={t(
                    'entityImport.nonImportableColumnsLabel'
                )}
                nonImportableLabel={
                    hasNonImportableColumns && showNonImportableColumns
                        ? t('translations:shown')
                        : t('translations:hidden')
                }
            >
                {this.renderStep()}
                {showResetConfirmationDialog ? (
                    <Dialog
                        theme={theme}
                        className={theme.resetDialog}
                        acceptActionLabel={t('translations:yes')}
                        cancelActionLabel={t('translations:no')}
                        active
                        title={t('entityImport.importAgain')}
                        onAccept={onResetImport}
                        onCancel={onResetCancel}
                        onDismiss={onResetCancel}
                    >
                        {t('entityImport.resetMessage')}
                    </Dialog>
                ) : null}
            </Dialog>
        );
    }
}

export default translate(['entityManagement', 'translations'])(
    themr(null, defaultTheme)(EntityImportView)
);
